// ==========================================
// BB — the mascot rig (2a)
// ==========================================
// One 48x48 rig, eight states. The antenna stem, head, visor and chin are constant;
// only the eyes, the antenna bulb colour and a handful of add-ons swap, so every
// state change is a 120ms crossfade of the same SVG rather than a new drawing.
//
// Sizes in use: 56 (collapsed bubble), 48 (expression sheet), 36 (panel header),
// 30 (glance), 28 (inline in messages), 22 (compact header, edge tab).
// ==========================================

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { BbState, GrowthStage } from '../models/buddy.types';
import { ACCENT_INK, BB_BODY, BB_BODY_SOFT, BB_VISOR, GROWTH_STAGES } from '../models/buddy.constants';

/**
 * Per-state accent. Expressive states own the colour; at rest BB shows his stage.
 *
 * These are the ink variants: BB's eyes and antenna are small shapes on a light
 * panel, so the saturated fill tones would wash out against white.
 */
const STATE_ACCENT: Readonly<Record<BbState, string | null>> = {
  idle: null, // falls back to the growth stage
  listening: ACCENT_INK.xp,
  thinking: ACCENT_INK.badge,
  speaking: ACCENT_INK.action,
  happy: ACCENT_INK.xp,
  celebratory: ACCENT_INK.streak,
  oops: ACCENT_INK.error,
  dozing: BB_BODY_SOFT,
};

const STATE_LABELS: Readonly<Record<BbState, string>> = {
  idle: 'BB is idle',
  listening: 'BB is listening',
  thinking: 'BB is thinking',
  speaking: 'BB is speaking',
  happy: 'BB is pleased — task complete',
  celebratory: 'BB is celebrating a milestone',
  oops: 'BB hit a problem',
  dozing: 'BB is dozing',
};

@Component({
  selector: 'app-bb-mascot',
  standalone: false,
  templateUrl: './bb-mascot.component.html',
  styleUrls: ['./bb-mascot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BbMascotComponent {
  @Input() state: BbState = 'idle';
  @Input() size = 36;
  @Input() stage: GrowthStage = 'scout';
  /**
   * True when the surrounding UI already announces BB's state in text, so the rig
   * should not repeat it to a screen reader.
   */
  @Input() decorative = false;

  get accent(): string {
    const stateAccent = STATE_ACCENT[this.state];
    if (stateAccent) {
      return stateAccent;
    }
    const def = GROWTH_STAGES.find((s) => s.stage === this.stage);
    return def ? def.antenna : ACCENT_INK.action;
  }

  /** Faint tint on the head outline, echoing the accent without adding a third colour. */
  get outline(): string {
    return this.state === 'idle' || this.state === 'dozing'
      ? 'rgba(17,24,39,.12)'
      : this.hexToRgba(this.accent, 0.38);
  }

  get label(): string {
    return STATE_LABELS[this.state];
  }

  /** Navy body, so BB reads as ProSuite's on a light panel. */
  get headFill(): string {
    return this.state === 'dozing' ? BB_BODY_SOFT : BB_BODY;
  }

  get chinFill(): string {
    switch (this.state) {
      case 'happy':
      case 'celebratory':
        return this.accent;
      default:
        return BB_BODY_SOFT;
    }
  }

  /** Antenna stem. */
  get stemStroke(): string {
    return this.state === 'dozing' ? '#9CA3AF' : BB_BODY_SOFT;
  }

  /** The visor stays near-black at every state — it is BB's screen. */
  get visorFill(): string {
    return BB_VISOR;
  }

  /** Closed eyes while dozing. */
  get restingEye(): string {
    return '#9CA3AF';
  }

  /** Second celebration mote, deliberately off-accent so the moment reads as special. */
  get moteAlt(): string {
    return ACCENT_INK.badge;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
